import { tags as t } from "@lezer/highlight";
import { createTheme } from "@uiw/codemirror-themes";

import type { Extension } from "@codemirror/state";

/**
 * Reads a Regal palette CSS custom property from the document body.
 */
function paletteColor(cssVar: string): string {
    return getComputedStyle(document.body).getPropertyValue(cssVar).trim();
}

/**
 * Builds a CodeMirror light theme from Regal palette CSS custom properties.
 *
 * Call this after the application styles are mounted so the CSS custom
 * properties are available to `getComputedStyle`.
 */
export function buildRegalCodeMirrorTheme(): Extension {
    const background = "#ffffff";
    const foreground = paletteColor("--gray-5") || "#000000";
    const fontFamily = getComputedStyle(document.body).fontFamily.trim() || 'Lato, "Helvetica Neue", sans-serif';

    return createTheme({
        theme: "light",
        settings: {
            background,
            foreground,
            caret: foreground,
            fontFamily,
            gutterBackground: background,
            gutterForeground: paletteColor("--gray-4") || foreground,
        },
        styles: [
            { tag: t.keyword, color: paletteColor("--purple-4") },
            { tag: [t.string, t.special(t.string)], color: paletteColor("--green-4") },
            { tag: t.number, color: paletteColor("--blue-4") },
            { tag: [t.typeName, t.className], color: paletteColor("--gold-5") },
            { tag: t.regexp, color: paletteColor("--red-3") },
            { tag: t.comment, color: paletteColor("--gray-4"), fontStyle: "italic" },
            { tag: [t.variableName, t.propertyName], color: paletteColor("--gray-5") },
            { tag: [t.punctuation, t.bracket], color: paletteColor("--gray-5") },
        ].filter((rule) => rule.color !== ""),
    });
}
